/**
 * Lo leído hace nada, para no volver a preguntarlo.
 *
 * Ir de Fotos a Álbumes y volver pedía `/api/albums` las tres veces. Cada
 * repetición es una petición a la API y media docena de consultas a la base
 * —el peaje que agotó el cupo de la cuenta— para traer exactamente lo que ya
 * estaba en memoria.
 *
 * Vive aquí, y no dentro del hook que la usa, porque hay **dos** que tienen que
 * tocarla y no pueden importarse entre ellos: el que lee (`useQuery`) y el
 * cliente HTTP, que la vacía en cuanto algo se escribe.
 *
 * Tres reglas la mantienen honesta:
 *
 *  1. **Dura poco.** Treinta segundos: lo justo para cubrir una navegación, no
 *     para que alguien vea una pantalla vieja. Un cambio hecho desde otro
 *     dispositivo tarda eso en aparecer, y no más.
 *  2. **Cualquier escritura la vacía entera.** Es la regla que descubrió la
 *     prueba en navegador: crear un álbum y volver a la lista enseñaba la lista
 *     de antes, sin él. Invalidar «sólo lo relacionado» exige una tabla de qué
 *     escritura toca qué listado, y esa tabla se queda desactualizada a la
 *     primera pantalla nueva. Vaciarlo todo cuesta unas cuantas lecturas
 *     repetidas; equivocarse cuesta enseñar datos que ya no existen.
 *  3. **Sólo guarda lecturas.** Un `POST` que en realidad lee —firmar
 *     miniaturas— se marca como tal en vez de invalidar nada.
 */
const VIDA_MS = 30_000;

interface Guardado {
  data: unknown;
  expira: number;
}

const lecturas = new Map<string, Guardado>();

export function claveDeLectura(
  path: string,
  query: Record<string, unknown> | undefined,
  key: string,
): string {
  return `${path}|${JSON.stringify(query ?? {})}|${key}`;
}

export function leerLectura<T>(clave: string): T | null {
  const guardado = lecturas.get(clave);
  if (!guardado) return null;

  if (guardado.expira <= Date.now()) {
    lecturas.delete(clave);
    return null;
  }

  return guardado.data as T;
}

/** Sólo se guarda lo que salió bien: un error guardado se serviría medio minuto. */
export function guardarLectura(clave: string, data: unknown): void {
  lecturas.set(clave, { data, expira: Date.now() + VIDA_MS });
}

/**
 * Vacía la caché entera.
 *
 * La llaman tres sitios: cualquier escritura —porque puede haber cambiado
 * cualquier listado—, el cierre de sesión —lo leído pertenece a quien lo pidió,
 * y en un ordenador compartido la siguiente persona no puede heredar ni el
 * nombre de un álbum— y los tests, que si no arrastran las respuestas de un
 * caso al siguiente.
 */
export function olvidarLecturas(): void {
  lecturas.clear();
}
